import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNotShowAgain } from 'src/core/hooks/useNotShowAgain';
import { DataHubService } from 'src/core/services/datahub';
import { ViewBulkDeleteSchema } from 'src/core/services/datahub/schemas/moduleList/viewBulkDeleteSchema';
import { ViewBulkPublishSchema } from 'src/core/services/datahub/schemas/moduleList/viewBulkPublishSchema';
import { ViewBulkUndoPublishSchema } from 'src/core/services/datahub/schemas/moduleList/viewBulkUndoPublishSchema';
import { BulkActionService } from 'src/modules/contentList/services';
import { BulkAction } from 'src/modules/contentList/types';
import { Toast } from 'src/shared/components/toast';
export function useBulkActions() {
    const { t } = useTranslation();
    const [activeBulkAction, setActiveBulkAction] = useState(null);
    const notShowAgain = {
        [BulkAction.Publish]: useNotShowAgain(BulkAction.Publish),
        [BulkAction.Unpublish]: useNotShowAgain(BulkAction.Unpublish),
        [BulkAction.Delete]: useNotShowAgain(BulkAction.Delete)
    };
    const mutation = {
        [BulkAction.Publish]: useMutation({ mutationFn: BulkActionService.publish }),
        [BulkAction.Unpublish]: useMutation({ mutationFn: BulkActionService.unpublish }),
        [BulkAction.Delete]: useMutation({ mutationFn: BulkActionService.delete })
    };
    const successMessage = {
        [BulkAction.Publish]: t('module_list.bulk.publish.success'),
        [BulkAction.Unpublish]: t('module_list.bulk.unpublish.success'),
        [BulkAction.Delete]: t('module_list.bulk.delete.success')
    };
    const datahubSchema = {
        [BulkAction.Publish]: ViewBulkPublishSchema,
        [BulkAction.Unpublish]: ViewBulkUndoPublishSchema,
        [BulkAction.Delete]: ViewBulkDeleteSchema
    };
    const onClose = () => {
        setActiveBulkAction(null);
        window.dispatchEvent(new CustomEvent('app-content-admin:bulkactionsmenu:closed'));
    };
    const onCancel = () => {
        setActiveBulkAction(null);
    };
    const onConfirm = (action, payload) => {
        return async (notShowAgainValue) => {
            if (!action) {
                return;
            }
            try {
                await mutation[action].mutateAsync(payload);
            }
            catch {
                Toast.show({ type: 'danger', message: t('error_boundary.title') });
                return;
            }
            const updateList = true;
            setActiveBulkAction(null);
            window.dispatchEvent(new CustomEvent('app-content-admin:bulkactionsmenu:closed', { detail: updateList }));
            Toast.show({ type: 'success', message: successMessage[action] });
            if (datahubSchema[action]) {
                DataHubService.sendEvent({
                    schema: new datahubSchema[action]({ arrivedFrom: window.location.href })
                });
            }
            if (notShowAgainValue) {
                notShowAgain[action].saveNotShowAgain();
            }
        };
    };
    const onClick = (action, payload) => {
        return () => {
            if (notShowAgain[action].notShowAgain.current) {
                onConfirm(action, payload)(false);
            }
            else {
                setActiveBulkAction(action);
            }
        };
    };
    return {
        activeBulkAction,
        onClose,
        onCancel,
        onConfirm,
        onClick,
        isPending: {
            [BulkAction.Publish]: mutation[BulkAction.Publish].isPending,
            [BulkAction.Unpublish]: mutation[BulkAction.Unpublish].isPending,
            [BulkAction.Delete]: mutation[BulkAction.Delete].isPending
        }
    };
}
